import { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useMutation, useQuery } from "@apollo/client";
import type { DocumentNode } from "@apollo/client";
import Button from "@/components/Button";
import Select from "@/components/Select";
import TextField from "@/components/TextField";
import Dialog from "@/components/Dialog";
import Icon from "@/components/Icon";
import ListItem from "@/components/ListItem/ListItem";
import {
  useAlgoliaSearch,
  AlgoliaSearchResults,
  type PersonHit,
  createPersonOnlySearchUiState,
  getPersonDisplayName,
  getPersonSearchHitSupportingText,
  isPersonExactMatch,
  createPersonOnlyHitRenderer
} from "@/components/AlgoliaSearch";
import { NotePriorityValues } from "@/types";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { CREATE_FACILITY_GOVERNANCE_ROLE_MUTATION } from "./mutations";
import { FACILITY_GOVERNANCE_TITLES_QUERY } from "./queries";
import { buildGovernanceTitleOptions, getGovernanceTitleFieldState, GOVERNANCE_ROLE_NOTE_TITLE } from "./_utils";

interface AddGovernanceRoleModalProps {
  open: boolean;
  facilityId: string;
  onClose: () => void;
  refetchQueries?: DocumentNode[];
}

interface FormValues {
  personId: string;
  governanceTitleId: string;
  internalNote: string;
}

const personSearchIndexName = import.meta.env.VITE_GLOBAL_PEOPLE_SEARCH_INDEX_NAME;

interface GovernanceRolePersonPickerProps {
  selectedPerson: PersonHit | null;
  onSelect: (hit: PersonHit) => void;
  onClear: () => void;
  error?: boolean;
  errorText?: string;
  disabled?: boolean;
}

function GovernanceRolePersonPicker({
  selectedPerson,
  onSelect,
  onClear,
  error,
  errorText,
  disabled
}: GovernanceRolePersonPickerProps) {
  const {
    query: personQuery,
    displayValue,
    results: personResults,
    isLoading: personLoading,
    showResults,
    highlightedIndex,
    setHighlightedIndex,
    handleQueryChange,
    handleClear: handleAlgoliaClear,
    handleItemSelected,
    setShowResults,
    setIsFocused,
    setDisplayValue,
    focusInput,
    itemRefs,
    resultsContainerRef,
    searchBoxRef
  } = useAlgoliaSearch<PersonHit>({
    indexName: personSearchIndexName,
    hitsPerPage: 7,
    initialUiState: createPersonOnlySearchUiState(personSearchIndexName),
    getDisplayValue: getPersonDisplayName,
    isExactMatch: isPersonExactMatch,
    onSelect
  });

  const hasActivePersonQuery = personQuery.trim() !== "";

  useEffect(() => {
    if (!selectedPerson) {
      focusInput();
    }
  }, [selectedPerson, focusInput]);

  useEffect(() => {
    if (displayValue) {
      setDisplayValue(undefined);
    }
  }, [displayValue, setDisplayValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchBoxRef.current &&
        !searchBoxRef.current.contains(event.target as Node) &&
        resultsContainerRef.current &&
        !resultsContainerRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchBoxRef, resultsContainerRef, setShowResults]);

  useEffect(() => {
    if (!showResults) return;

    const handleGlobalKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setShowResults(false);
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => {
      window.removeEventListener("keydown", handleGlobalKeyDown);
    };
  }, [showResults, setShowResults]);

  const handleClearPerson = () => {
    handleAlgoliaClear();
    setDisplayValue(undefined);
    onClear();
    requestAnimationFrame(() => focusInput());
  };

  const personTrailingIcon =
    displayValue || personQuery ? (
      <button
        type="button"
        className="cursor-pointer flex items-center h-full"
        onClick={handleClearPerson}
        aria-label="Clear search query"
        disabled={disabled}
      >
        <Icon name="cancel" />
      </button>
    ) : undefined;

  return (
    <div className="space-y-4">
      {!selectedPerson && (
        <div className="relative">
          <div ref={searchBoxRef}>
            <TextField
              label="Person"
              placeholder="Search by name, PGA ID, or email"
              value={displayValue || personQuery}
              onChangeText={(value) => {
                setDisplayValue(undefined);
                handleQueryChange(value);
              }}
              onFocus={() => {
                setIsFocused(true);
                if (personQuery.trim() !== "") {
                  setShowResults(true);
                }
              }}
              onBlur={() => setIsFocused(false)}
              leadingIcon="search"
              trailingIcon={personTrailingIcon}
              variant="outlined"
              className="w-full"
              required
              error={error}
              errorText={errorText}
              disabled={disabled}
            />
          </div>

          {showResults && hasActivePersonQuery && (
            <AlgoliaSearchResults
              results={personResults}
              query={personQuery}
              isLoading={personLoading}
              highlightedIndex={highlightedIndex}
              setHighlightedIndex={setHighlightedIndex}
              handleItemSelected={handleItemSelected}
              itemRefs={itemRefs}
              resultsContainerRef={resultsContainerRef}
              renderHit={createPersonOnlyHitRenderer()}
              isExactMatch={isPersonExactMatch}
              className="absolute z-50 mt-1 w-full bg-white shadow-md rounded-md p-3 max-h-80 overflow-y-auto"
            />
          )}
        </div>
      )}

      {selectedPerson && (
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <ListItem
              compact
              useAvatar
              picture={selectedPerson.profile_photo}
              headline={getPersonDisplayName(selectedPerson)}
              supportingText={getPersonSearchHitSupportingText(selectedPerson)}
            />
          </div>
          <button
            type="button"
            className="cursor-pointer flex items-center shrink-0 p-1 rounded-full hover:bg-sys-brand-surface-container-high transition-colors"
            onClick={handleClearPerson}
            aria-label="Remove selected person"
            disabled={disabled}
          >
            <Icon name="close" size={20} />
          </button>
        </div>
      )}
    </div>
  );
}

export function AddGovernanceRoleModal({
  open,
  facilityId,
  onClose,
  refetchQueries = []
}: AddGovernanceRoleModalProps) {
  const [personSearchKey, setPersonSearchKey] = useState(0);
  const [selectedPerson, setSelectedPerson] = useState<PersonHit | null>(null);
  const [createFacilityGovernanceRole, { loading: saving }] = useMutation(CREATE_FACILITY_GOVERNANCE_ROLE_MUTATION, {
    refetchQueries
  });
  const {
    data: titlesData,
    loading: titlesLoading,
    error: titlesError
  } = useQuery(FACILITY_GOVERNANCE_TITLES_QUERY, { fetchPolicy: "cache-first" });
  const governanceTitleOptions = useMemo(
    () => buildGovernanceTitleOptions(titlesData?.facilityGovernanceTitles),
    [titlesData?.facilityGovernanceTitles]
  );
  const titleFieldState = getGovernanceTitleFieldState({
    loading: titlesLoading,
    hasError: Boolean(titlesError),
    optionCount: governanceTitleOptions.length
  });

  const { control, handleSubmit, reset, setValue, watch } = useForm<FormValues>({
    defaultValues: {
      personId: "",
      governanceTitleId: "",
      internalNote: ""
    }
  });

  const selectedPersonId = watch("personId");
  const selectedGovernanceTitleId = watch("governanceTitleId");

  useEffect(() => {
    if (!selectedGovernanceTitleId && governanceTitleOptions.length > 0) {
      setValue("governanceTitleId", governanceTitleOptions[0].value);
    }
  }, [governanceTitleOptions, selectedGovernanceTitleId, setValue]);

  const handleClose = () => {
    reset();
    setSelectedPerson(null);
    setPersonSearchKey((key) => key + 1);
    onClose();
  };

  const onSubmit = async (values: FormValues) => {
    const content = values.internalNote.trim();

    try {
      const { data } = await createFacilityGovernanceRole({
        variables: {
          input: {
            personId: values.personId,
            facilityId,
            facilityGovernanceTitleId: values.governanceTitleId,
            note: content ? { content, priority: NotePriorityValues.NONE, title: GOVERNANCE_ROLE_NOTE_TITLE } : null
          }
        }
      });

      if (!data?.createFacilityGovernanceRole.success) {
        showErrorSnackbar(data?.createFacilityGovernanceRole.message || "Unable to add governance role.");
        return;
      }

      showSnackbar("Governance role added");
      handleClose();
    } catch {
      showErrorSnackbar("Unable to add governance role.");
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClosed={handleClose}
      headline="Add Governance Role"
      className="w-[640px]"
      hasFormElements
      displayOverflow
      actions={
        <>
          <Button variant="text" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit(onSubmit)} disabled={saving || !selectedPersonId || !selectedGovernanceTitleId}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <p className="text-body-medium text-sys-on-surface">
          Search for a person and assign a governance role at this facility.
        </p>

        <Controller
          name="personId"
          control={control}
          rules={{ required: "Person is required" }}
          render={({ fieldState }) => (
            <GovernanceRolePersonPicker
              key={personSearchKey}
              selectedPerson={selectedPerson}
              disabled={saving}
              error={fieldState.invalid}
              errorText={fieldState.error?.message}
              onSelect={(hit) => {
                setSelectedPerson(hit);
                setValue("personId", hit.objectID, { shouldValidate: true });
              }}
              onClear={() => {
                setSelectedPerson(null);
                setValue("personId", "", { shouldValidate: true });
              }}
            />
          )}
        />

        <Controller
          name="governanceTitleId"
          control={control}
          rules={{ required: "Role is required" }}
          render={({ field: { onChange, value }, fieldState }) => (
            <Select
              label="Role"
              options={governanceTitleOptions}
              value={value}
              onSelect={onChange}
              disabled={titleFieldState.disabled}
              error={fieldState.invalid || Boolean(titleFieldState.errorText)}
              errorText={titleFieldState.errorText ?? fieldState.error?.message}
              supportingText={titleFieldState.supportingText}
              required
            />
          )}
        />

        <Controller
          name="internalNote"
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextField
              type="textarea"
              rows={3}
              label="Internal Note (Optional)"
              value={value}
              onChangeText={onChange}
              className="w-full"
            />
          )}
        />
      </div>
    </Dialog>
  );
}
